import { Link } from 'react-router-dom';
import { buildLogsHref } from '@/features/admin/lib/logsLink';

/** Enough to name the broken feeds without turning the toast into a wall of text. */
const MAX_LISTED_ERRORS = 3;

interface FeedErrorToastProps {
  count: number;
  errors: string[];
  traceId?: string;
  startedAt?: string;
}

/**
 * Body of the "some feeds could not be read" toast after a manual run. Replaces the old
 * "See the server log." dead end with a link straight to that run's lines in Admin → Logs.
 * The error strings come from the server, so they render as text only.
 */
export function FeedErrorToast({ count, errors, traceId, startedAt }: FeedErrorToastProps) {
  const listed = errors.slice(0, MAX_LISTED_ERRORS);
  const more = count - listed.length;

  return (
    <div className="space-y-1 text-sm">
      <p className="font-medium">
        {count} {count === 1 ? 'feed' : 'feeds'} could not be read:
      </p>
      <ul className="space-y-0.5">
        {listed.map((error, i) => (
          <li key={i} className="break-words text-muted-foreground">
            {error}
          </li>
        ))}
      </ul>
      {more > 0 && <p className="text-muted-foreground">…and {more} more</p>}
      <Link to={buildLogsHref({ traceId, from: startedAt })} className="inline-block font-medium text-primary hover:underline">
        View in Logs
      </Link>
      <TraceRef traceId={traceId} />
    </div>
  );
}

/**
 * The full trace id, selectable, so it still finds the run after the toast is gone: paste it
 * into the Trace id box in Admin → Logs.
 */
function TraceRef({ traceId }: { traceId?: string }) {
  if (!traceId) return null;
  return (
    <p className="break-all font-mono text-xs text-muted-foreground">
      Trace id: <span className="select-all">{traceId}</span>
    </p>
  );
}

interface RequestFailedToastProps {
  message: string;
  traceId?: string;
  startedAt?: string;
}

/**
 * Body of the toast when a whole manual run failed (a 500, or no response at all). Links to the
 * failing request's lines when the response carried a trace id, else to warnings since the start.
 */
export function RequestFailedToast({ message, traceId, startedAt }: RequestFailedToastProps) {
  return (
    <div className="space-y-1 text-sm">
      <p className="break-words font-medium">{message}</p>
      <Link to={buildLogsHref({ traceId, from: startedAt })} className="inline-block font-medium text-primary hover:underline">
        View in Logs
      </Link>
      <TraceRef traceId={traceId} />
    </div>
  );
}
